import { CriteriaPaneProps, CriteriaState } from '../types';
import {
  CriteriaRow,
  CriteriaRows,
  CriteriaSection,
  FieldGrid,
  IncludeExcludeField,
  Input,
  MultiSelect,
  SelectSimple,
  Switch,
  TextArea,
  ZipTextareaStats,
} from '../FormComponents';
import { createFieldUpdater, formatOptions } from '../helpers';
import { anuraScores, browserTypes, genders, userDataFields } from '../constants';
import {
  useAibListOptions,
  useBrowserOptions,
  useDeviceOptions,
  useDomainNameOrTagOptions,
  useOperatingSystemOptions,
} from '../useCriteriaOptions';
import states from '../../../../utils/states';
import { HELP } from '../criteriaHelp';
import { errorReader, hasValue, isIncludeExclude } from './shared';
import type { CriteriaSectionDef } from './types';
import { PreviousRegistrationSection } from './PreviousRegistrationSection';

// Control widths for single-value rows: a number needs little room, a select its longest option.
const NUMBER_WIDTH = '8.5rem';
const SELECT_WIDTH = '13rem';

const nonPublisher = ({ isPublisher }: { isPublisher: boolean }) => !isPublisher;

const STATE_OPTIONS = states.map((state) => ({ label: state, value: state }));
const DIGIT_OPTIONS = formatOptions.fromNumbers(0, 9);
const MOBILE_BROWSERS = ['Chrome', 'Opera', 'Firefox', 'Safari', 'IE', 'Other'].map((b) => ({ label: b, value: b }));

/* ---------------------------------------------------------------- Compliance & Verification */

function ComplianceSection({ state, setState, sourceType }: CriteriaPaneProps) {
  const isPublisher = sourceType === 'publisherPrePingCriteria';
  const toggle = (field: keyof CriteriaState, id: string, label: string, tooltip?: string) => (
    <Switch
      id={id}
      label={label}
      checked={Boolean(state[field])}
      onChange={createFieldUpdater(setState, field) as (v: boolean) => void}
      tooltip={tooltip}
    />
  );

  return (
    <CriteriaSection title='Compliance & Verification' help={HELP.compliance}>
      <FieldGrid min='17rem'>
        {!isPublisher && toggle('tcpaOptInRequired', 'optin', 'TCPA Opt-in Required')}
        {toggle('tcpaLastOptInInterval', 'optin-interval', 'TCPA Opt-in Required Over Last Interval')}
        {toggle('briteVerifyPhoneValidated', 'bv-phone', 'Brite Verify Phone Validated')}
        {toggle('briteVerifyEmailValidated', 'bv-email', 'Brite Verify Email Validated')}
        {toggle('phoneBlacklist', 'blacklist', 'Include users in the phone Blacklist')}
        {toggle('gpc', 'gpc', 'Hide offer for GPC user', HELP.gpc)}
        {toggle('englishBrowserLanguageRequired', 'english', 'English Browser Language Required')}
      </FieldGrid>
    </CriteriaSection>
  );
}

/* ------------------------------------------------------------------------------ Demographics */

function DemographicsSection({ state, setState, sourceType, errors }: CriteriaPaneProps) {
  const isPublisher = sourceType === 'publisherPrePingCriteria';
  const { first } = errorReader(errors);

  return (
    <CriteriaSection title='Demographics'>
      <CriteriaRows>
        <CriteriaRow label='Min Age' help={HELP.age} controlMaxWidth={NUMBER_WIDTH}>
          <Input
            label='Min Age'
            hideLabel
            type='number'
            value={state.minAge}
            onChange={createFieldUpdater(setState, 'minAge')}
            error={first(['minAge'], ['criteriaMinAge'])}
          />
        </CriteriaRow>
        <CriteriaRow label='Max Age' help={HELP.age} controlMaxWidth={NUMBER_WIDTH}>
          <Input
            label='Max Age'
            hideLabel
            type='number'
            value={state.maxAge}
            onChange={createFieldUpdater(setState, 'maxAge')}
            error={first(['maxAge'], ['criteriaMaxAge'])}
          />
        </CriteriaRow>
        {!isPublisher && (
          <CriteriaRow label='Exclude Browser Type' help={HELP.excludeBrowserType} controlMaxWidth={SELECT_WIDTH}>
            <SelectSimple
              label='Exclude Browser Type'
              hideLabel
              value={state.browserType}
              onChange={createFieldUpdater(setState, 'browserType')}
              options={browserTypes}
            />
          </CriteriaRow>
        )}
        <CriteriaRow label='Exclude Gender' help={HELP.excludeGender} controlMaxWidth={SELECT_WIDTH}>
          <SelectSimple
            label='Exclude Gender'
            hideLabel
            value={state.genderValue}
            onChange={createFieldUpdater(setState, 'genderValue')}
            options={genders}
          />
        </CriteriaRow>
        <CriteriaRow label='Exclude Anura Score' help={HELP.anuraScore} controlMaxWidth={SELECT_WIDTH}>
          <SelectSimple
            label='Exclude Anura Score'
            hideLabel
            value={state.anuraScore}
            onChange={createFieldUpdater(setState, 'anuraScore')}
            options={anuraScores}
          />
        </CriteriaRow>
      </CriteriaRows>
    </CriteriaSection>
  );
}

/* -------------------------------------------------------------------------- User Data & Lists */

function UserDataSection({ state, setState, errors, markFieldTouched }: CriteriaPaneProps) {
  const { first } = errorReader(errors);
  const aibLists = useAibListOptions();

  return (
    <CriteriaSection title='User Data & Lists'>
      <CriteriaRows>
        <IncludeExcludeField
          label='Required User Data'
          help={HELP.requiredUserData}
          mode={state.requiredUserDataIncludeExclude}
          onModeChange={createFieldUpdater(setState, 'requiredUserDataIncludeExclude')}
          modeError={first(['criteriaUserDataIncludeExclude'])}
        >
          <MultiSelect
            ariaLabel='Required User Data Fields'
            value={state.requiredUserDataValues}
            onChange={createFieldUpdater(setState, 'requiredUserDataValues')}
            options={userDataFields}
            error={first(['criteriaRequiredUserData'])}
          />
        </IncludeExcludeField>
        <IncludeExcludeField
          label='Null Source New Users'
          help={HELP.nullSourceNewUsers}
          mode={state.nullSourceNewUserIncludeExclude}
          onModeChange={createFieldUpdater(setState, 'nullSourceNewUserIncludeExclude')}
        />
        <IncludeExcludeField
          label='AIB List'
          help={HELP.aibList}
          mode={state.aibListIncludeExclude}
          onModeChange={createFieldUpdater(setState, 'aibListIncludeExclude')}
          modeError={first(['criteriaAibList'])}
        >
          <MultiSelect
            ariaLabel='AIB Items'
            value={state.aibList}
            onChange={createFieldUpdater(setState, 'aibList')}
            options={aibLists}
            error={first(['criteriaAibList'])}
            onBlur={() => markFieldTouched('criteriaAibList')}
          />
        </IncludeExcludeField>
      </CriteriaRows>
    </CriteriaSection>
  );
}

/* ------------------------------------------------------------------------------------- IPQS */

const IPQS_FIELDS = [
  { key: 'email', label: 'Email' },
  { key: 'ip', label: 'IP' },
  { key: 'phone', label: 'Phone' },
  { key: 'fingerprint', label: 'Fingerprint' },
] as const;

function IpqsSection({ state, setState, errors }: CriteriaPaneProps) {
  const { get } = errorReader(errors);
  const ipqs = state.ipqsConfiguration;
  const update = (patch: Partial<CriteriaState['ipqsConfiguration']>) =>
    setState((s) => ({ ...s, ipqsConfiguration: { ...s.ipqsConfiguration, ...patch } }));

  return (
    <CriteriaSection title='IPQS Fraud Scores' help={HELP.ipqs}>
      <Switch
        id='ipqs-enabled'
        label='Enable IPQS Configuration'
        checked={ipqs.shouldProcess}
        onChange={(checked) => update({ shouldProcess: checked })}
      />
      {ipqs.shouldProcess && (
        <FieldGrid min='9rem'>
          {IPQS_FIELDS.map(({ key, label }) => (
            <Input
              key={key}
              label={label}
              type='number'
              value={ipqs[key]}
              onChange={(v) => update({ [key]: v })}
              error={get(['ipqsConfiguration', key])}
              help={HELP.ipqsThreshold}
            />
          ))}
        </FieldGrid>
      )}
    </CriteriaSection>
  );
}

/* ------------------------------------------------------------------------- Device & Domain */

function DeviceDomainSection({ state, setState, sourceType, errors, markFieldTouched }: CriteriaPaneProps) {
  const isPublisher = sourceType === 'publisherPrePingCriteria';
  const { first } = errorReader(errors);
  const devices = useDeviceOptions();
  const desktopBrowsers = useBrowserOptions();
  const operatingSystems = useOperatingSystemOptions();
  const domainsOrTags = useDomainNameOrTagOptions();
  // Mobile traffic reports a short, fixed set of browser families.
  const browsers = state.browserType === 'Mobile' ? MOBILE_BROWSERS : desktopBrowsers;

  return (
    <CriteriaSection title='Device & Domain'>
      <CriteriaRows>
        <IncludeExcludeField
          label='User_ID last digit'
          help={HELP.userIdLastDigit}
          mode={state.userIdLastDigitIncludeExclude}
          onModeChange={createFieldUpdater(setState, 'userIdLastDigitIncludeExclude')}
        >
          <MultiSelect
            ariaLabel='Digits'
            value={state.userIdLastDigitValues || []}
            onChange={createFieldUpdater(setState, 'userIdLastDigitValues')}
            options={DIGIT_OPTIONS}
            allowCreate
            error={first(['criteriaUserIdLastDigitString'])}
            onBlur={() => markFieldTouched('criteriaUserIdLastDigitString')}
          />
        </IncludeExcludeField>
        {!isPublisher && (
          <>
            <IncludeExcludeField
              label='Device'
              mode={state.devicesIncludeExclude}
              onModeChange={createFieldUpdater(setState, 'devicesIncludeExclude')}
            >
              <MultiSelect
                ariaLabel='Devices'
                value={state.devices}
                onChange={createFieldUpdater(setState, 'devices')}
                options={devices}
                error={first(['criteriaDevices'])}
                onBlur={() => markFieldTouched('criteriaDevices')}
              />
            </IncludeExcludeField>
            <IncludeExcludeField
              label='Browsers'
              mode={state.browsersIncludeExclude}
              onModeChange={createFieldUpdater(setState, 'browsersIncludeExclude')}
            >
              <MultiSelect
                ariaLabel='Browsers'
                value={state.browsers}
                onChange={createFieldUpdater(setState, 'browsers')}
                options={browsers}
                error={first(['criteriaBrowsers'])}
                onBlur={() => markFieldTouched('criteriaBrowsers')}
              />
            </IncludeExcludeField>
            <IncludeExcludeField
              label='What If Domain'
              help={HELP.whatIfDomain}
              mode={state.domainsIncludeExclude}
              onModeChange={createFieldUpdater(setState, 'domainsIncludeExclude')}
            >
              <MultiSelect
                ariaLabel='Domains/Tags'
                value={state.domains}
                onChange={createFieldUpdater(setState, 'domains')}
                options={domainsOrTags}
                error={first(['criteriaDomains'])}
                onBlur={() => markFieldTouched('criteriaDomains')}
              />
            </IncludeExcludeField>
            <IncludeExcludeField
              label='Operating System'
              mode={state.operatingSystemIncludeExclude}
              onModeChange={createFieldUpdater(setState, 'operatingSystemIncludeExclude')}
            >
              <MultiSelect
                ariaLabel='Operating Systems'
                value={state.operatingSystems}
                onChange={createFieldUpdater(setState, 'operatingSystems')}
                options={operatingSystems}
                error={first(['criteriaOperatingSystems'])}
                onBlur={() => markFieldTouched('criteriaOperatingSystems')}
              />
            </IncludeExcludeField>
          </>
        )}
      </CriteriaRows>
    </CriteriaSection>
  );
}

/* ---------------------------------------------------------------------------------- Location */

function LocationSection({ state, setState, errors, markFieldTouched }: CriteriaPaneProps) {
  const { first } = errorReader(errors);

  return (
    <CriteriaSection title='Location'>
      <CriteriaRows>
        <IncludeExcludeField
          label='States'
          mode={state.statesIncludeExclude}
          onModeChange={createFieldUpdater(setState, 'statesIncludeExclude')}
        >
          <MultiSelect
            ariaLabel='States'
            value={state.statesValues}
            onChange={createFieldUpdater(setState, 'statesValues')}
            options={STATE_OPTIONS}
            error={first(['criteriaStates'])}
            onBlur={() => markFieldTouched('criteriaStates')}
          />
        </IncludeExcludeField>
        <IncludeExcludeField
          label='Zip Code'
          help={HELP.zipCodes}
          mode={state.zipsIncludeExclude}
          onModeChange={createFieldUpdater(setState, 'zipsIncludeExclude')}
        >
          <ZipTextareaStats
            hideLabel
            value={state.zipsValues}
            onChange={createFieldUpdater(setState, 'zipsValues')}
            error={first(['criteriaZips'])}
            onBlur={() => markFieldTouched('criteriaZips')}
          />
        </IncludeExcludeField>
      </CriteriaRows>
    </CriteriaSection>
  );
}

/* ------------------------------------------------------------------------------ Job Keywords */

function JobKeywordsSection({ state, setState }: CriteriaPaneProps) {
  const keywords = state.jobKeyword.jobKeywords || [];

  return (
    <CriteriaSection title='Job Keywords'>
      <IncludeExcludeField
        label='Job Keyword'
        help={HELP.jobKeywords}
        mode={state.jobKeyword.includeExclude}
        onModeChange={(v) =>
          setState((s) => ({
            ...s,
            jobKeyword: { ...s.jobKeyword, includeExclude: v, jobKeywords: s.jobKeyword.jobKeywords || [] },
          }))
        }
      >
        <TextArea
          label='Job Keywords'
          hideLabel
          value={keywords.join('\n')}
          onChange={(v) => setState((s) => ({ ...s, jobKeyword: { ...s.jobKeyword, jobKeywords: v.split('\n') } }))}
          placeholder='One keyword per line'
        />
      </IncludeExcludeField>
    </CriteriaSection>
  );
}

/* ---------------------------------------------------------------------------------- Registry */

export const USERS_SECTIONS: CriteriaSectionDef[] = [
  {
    id: 'compliance',
    tab: 'users',
    title: 'Compliance & Verification',
    fields: [
      'TCPA Opt-in Required Over Last Interval',
      'Brite Verify Phone Validated',
      'Brite Verify Email Validated',
      'Include users in the phone Blacklist',
      'Hide offer for GPC user',
      'English Browser Language Required',
    ],
    aliases: [
      'TCPA Opt-in Required',
      'tcpa',
      'consent',
      'opt in',
      'briteverify',
      'blacklist',
      'gpc',
      'privacy',
      'language',
      'english',
    ],
    stateKeys: [
      'tcpaOptInRequired',
      'tcpaLastOptInInterval',
      'briteVerifyPhoneValidated',
      'briteVerifyEmailValidated',
      'phoneBlacklist',
      'gpc',
      'englishBrowserLanguageRequired',
    ],
    isConfigured: (s, { isPublisher }) =>
      (!isPublisher && s.tcpaOptInRequired) ||
      s.tcpaLastOptInInterval ||
      s.briteVerifyPhoneValidated ||
      s.briteVerifyEmailValidated ||
      s.phoneBlacklist ||
      s.gpc ||
      s.englishBrowserLanguageRequired,
    Component: ComplianceSection,
  },
  {
    id: 'demographics',
    tab: 'users',
    title: 'Demographics',
    fields: ['Min Age', 'Max Age', 'Exclude Gender', 'Exclude Anura Score'],
    aliases: ['Exclude Browser Type', 'age', 'dob', 'gender', 'anura', 'fraud', 'mobile', 'desktop'],
    stateKeys: ['minAge', 'maxAge', 'browserType', 'genderValue', 'anuraScore'],
    isConfigured: (s, { isPublisher }) =>
      hasValue(s.minAge) ||
      hasValue(s.maxAge) ||
      hasValue(s.genderValue) ||
      (!isPublisher && hasValue(s.browserType)) ||
      hasValue(s.anuraScore),
    Component: DemographicsSection,
  },
  {
    id: 'userData',
    tab: 'users',
    title: 'User Data & Lists',
    fields: ['Required User Data', 'Null Source New Users', 'AIB List'],
    aliases: ['user fields', 'email', 'phone', 'ip zip', 'aib', 'new users', 'null source'],
    stateKeys: [
      'requiredUserDataIncludeExclude',
      'requiredUserDataValues',
      'aibListIncludeExclude',
      'aibList',
      'nullSourceNewUserIncludeExclude',
    ],
    appliesTo: nonPublisher,
    isConfigured: (s) =>
      isIncludeExclude(s.requiredUserDataIncludeExclude) ||
      isIncludeExclude(s.aibListIncludeExclude) ||
      isIncludeExclude(s.nullSourceNewUserIncludeExclude),
    Component: UserDataSection,
  },
  {
    id: 'previousRegistrations',
    tab: 'users',
    title: 'Previous Registrations',
    fields: ['AND/OR logic'],
    aliases: ['Min', 'Max', 'registrations', 'registered', 'history', 'domains', 'affiliates'],
    stateKeys: ['previousRegistrations'],
    // The logic defaults to AND, so it is the entries that make this configured.
    isConfigured: (s) =>
      Boolean(s.previousRegistrations?.andOrLogic) && hasValue(s.previousRegistrations?.previousRegistrations),
    Component: PreviousRegistrationSection,
  },
  {
    id: 'ipqs',
    tab: 'users',
    title: 'IPQS Fraud Scores',
    fields: ['Enable IPQS Configuration'],
    aliases: ['Email', 'IP', 'Phone', 'Fingerprint', 'ipqs', 'fraud score'],
    stateKeys: ['ipqsConfiguration'],
    appliesTo: nonPublisher,
    isConfigured: (s) => Boolean(s.ipqsConfiguration?.shouldProcess),
    Component: IpqsSection,
  },
  {
    id: 'deviceDomain',
    tab: 'users',
    title: 'Device & Domain',
    fields: ['User_ID last digit'],
    aliases: [
      'Device',
      'Browsers',
      'Operating System',
      'What If Domain',
      'user id',
      'digit',
      'os',
      'browser',
      'domain',
      'mobile',
    ],
    stateKeys: [
      'userIdLastDigitIncludeExclude',
      'userIdLastDigitValues',
      'devicesIncludeExclude',
      'devices',
      'browsersIncludeExclude',
      'browsers',
      'browserType',
      'domainsIncludeExclude',
      'domains',
      'operatingSystemIncludeExclude',
      'operatingSystems',
    ],
    isConfigured: (s, { isPublisher }) =>
      isIncludeExclude(s.userIdLastDigitIncludeExclude) ||
      (!isPublisher &&
        (isIncludeExclude(s.devicesIncludeExclude) ||
          isIncludeExclude(s.browsersIncludeExclude) ||
          isIncludeExclude(s.operatingSystemIncludeExclude) ||
          isIncludeExclude(s.domainsIncludeExclude))),
    Component: DeviceDomainSection,
  },
  {
    id: 'location',
    tab: 'users',
    title: 'Location',
    fields: ['States', 'Zip Code'],
    aliases: ['geo', 'state', 'zip', 'postal', 'location'],
    stateKeys: ['statesIncludeExclude', 'statesValues', 'zipsIncludeExclude', 'zipsValues'],
    isConfigured: (s) => isIncludeExclude(s.statesIncludeExclude) || isIncludeExclude(s.zipsIncludeExclude),
    Component: LocationSection,
  },
  {
    id: 'jobKeywords',
    tab: 'users',
    title: 'Job Keywords',
    fields: ['Job Keyword'],
    aliases: ['jobs', 'keywords', 'search term'],
    stateKeys: ['jobKeyword'],
    appliesTo: nonPublisher,
    isConfigured: (s) => isIncludeExclude(s.jobKeyword?.includeExclude),
    Component: JobKeywordsSection,
  },
];
